import {
  castCompatibleValue,
  CompatibilityResult,
  CompatibleValue,
  FlatCompatibleValue,
  isOption,
  noRestrictions,
  restrictedTo
} from './CompatibleValues.js';
import { Model, pOptionTypeGuard, PropertyPath } from '../engine.js';
import { PropertyPathWalker } from '../internal/PropertyPathWalker.js';
import { Schema } from '../schema.js';
import { objectTypeTypeGuard, TypeReference } from '../types.js';
import { optionIdSymbol } from '../engine-impl.js';
import { ExternalFieldRegistry } from '../services/ExternalFieldRegistry.js';
import { TypeService } from '../services/TypeService.js';
import { engineUtils } from '../services/EngineUtils.js';
import { Option } from '../option.js'


export type ColumnName = PropertyPath;
export type CompatibilityRule = CompatibleValue[]
export type Hash = string;


function isModelValueOption(modelValue: any) {
  return !!modelValue[optionIdSymbol];
}

export class CompatibilityTable {
  rules: CompatibilityRule[] = [];
  columnName: Map<ColumnName, number> = new Map<ColumnName, number>();
  private externalFieldRegistry: ExternalFieldRegistry = null;
  private typeService: TypeService;
  private validHashes = new Set<Hash>();
  private whenExpression: (model: Model) => boolean = () => true;
  private optionsColumns = new Map<PropertyPath, Set<ColumnName>>();

  constructor(private structure: CompatibleTableStructure,
              private schema: Schema,
              public readonly typeRef: TypeReference) {
    this.typeService = new TypeService(this.typeRef, this.schema);
    for (let i = 0; i < structure.columns.length; i++) {
      this.columnName.set(structure.columns[i], i);
    }

    this.buildOptionColumns();
  }


  setWhen(when: (model: Model) => boolean) {
    this.whenExpression = when;
  }

  injectExternalFieldRegistry(registry: ExternalFieldRegistry) {
    this.externalFieldRegistry = registry;
  }

  addCompatibilityRules(...rules: CompatibilityRule[]) {
    for (let i = 0; i < rules.length; i++) {
      try {
        let rule = rules[i];
        this.validateRule(rule);
        this.rules.push(rule);
        this.addRuleHash(rule);
      } catch (e) {
        if (e instanceof Error) {
          throw new Error(`Invalid ${i} rule. ${e.message}`);
        } else {
          throw e;
        }
      }
    }
  }

  private validateRule(rule: CompatibilityRule) {
    if (rule.length < this.structure.columns.length) {
      throw new Error(`Invalid count of values. ${this.structure.columns.length} expected but received ${rule.length}`);
    }
    //TODO: Add validation
  }

  getAvailableCompatibleValues(propertyPath: PropertyPath): CompatibilityResult {
    const values = new Set<CompatibleValue>();
    for (let rule of this.rules) {
      const rulePropertyValue = this.findPropertyPathValue(rule, propertyPath);
      if (rulePropertyValue !== undefined) {
        values.add(rulePropertyValue);
      }
    }
    let restrictedToValues = castCompatibleValue([...values]);
    return values.size > 0 ? restrictedTo(restrictedToValues) : noRestrictions();
  }

  getCompatibleWithModelValues(model: Model, propertyPath: PropertyPath) {
    if (this.isCompatibilitySwitchedOff(model)) {
      return noRestrictions();
    }
    this.validateModelPropertyPath(model, propertyPath);
    const modelRow = this.getModelRow(model);
    const values = new Set<CompatibleValue>();
    for (let rule of this.rules) {
      const rulePropertyValue = this.findPropertyPathValue(rule, propertyPath);
      if (rulePropertyValue !== undefined && this.modelSatisfiesRule(modelRow, rule, propertyPath)) {
        values.add(rulePropertyValue);
      }
    }
    let restrictedToValues = castCompatibleValue([...values]);
    return values.size > 0 ? restrictedTo(restrictedToValues) : noRestrictions();
  }

  private getModelRow(model: Model) {
    return this.structure.columns.map(columnName => {
      const propertyPathWalker = new PropertyPathWalker(columnName, this.schema, model, this.typeRef);
      const propertyValue = propertyPathWalker.getPropertyValue();
      // common model properties
      if (propertyValue) {
        return propertyValue.value;
      }

      // external field columns
      if (this.isExternalField(columnName)) {
        return this.getExternalFieldValue(model, columnName);
      } else {
        throw new TypeError(`Invalid propertyPath ${columnName}`);
      }
    })
  }

  // Options are replaced with their ids
  private getModelRowValue(model: Model): FlatCompatibleValue[] {
    return this.getModelRow(model).map(v => isModelValueOption(v) ? v[optionIdSymbol] : v);
  }

  private getRuleRowValue(rule: CompatibilityRule): FlatCompatibleValue[] {
    return rule.map(v => isOption(v) ? v._id : v);
  }

  private findPropertyPathValue(rule: CompatibilityRule, propertyPath: PropertyPath) {
    const index = this.columnName.get(propertyPath);
    return rule[index];
  }

  private modelSatisfiesRule(modelValues: any[], rule: CompatibilityRule, ignoreProperty: PropertyPath) {
    for (let i = 0; i < this.structure.columns.length; i++) {
      const key = this.structure.columns[i];
      let ruleValue = rule[i];
      let modelValue = modelValues[i];
      if (key !== ignoreProperty) {

        if (isOption(ruleValue)) {
          if (isModelValueOption(modelValue)) {
            ruleValue = ruleValue._id;
            modelValue = modelValue[optionIdSymbol];
          } else return false;
        }
        if (ruleValue !== modelValue) {
          return false;
        }
      }
    }
    return true;
  }

  private validateModelPropertyPath(model: Model, propertyPath: PropertyPath) {
    const propertyPathWalker = new PropertyPathWalker(propertyPath, this.schema, model, this.typeRef);
    const propertyValue = propertyPathWalker.getPropertyValue();
    if (propertyValue === undefined && !this.isExternalField(propertyPath)) {
      throw new TypeError(`Invalid propertyPath ${propertyPath}`);
    }
  }

  private isExternalField(columnName: ColumnName) {
    return this.typeService.isPropertyExternal(columnName)
  }

  private getExternalFieldValue(model: Model, columnName: ColumnName) {
    if (this.externalFieldRegistry) {
      return this.externalFieldRegistry.getExternalFieldValue(model, columnName);
    }
    throw new Error(`External wasn't injected into CompatibilityTable`)
  }

  validate(model: Model): string | null {
    if (this.isCompatibilitySwitchedOff(model)) {
      return null;
    }
    const modelValue = this.getModelRowValue(model);
    const modelHash = this.getHash(modelValue);
    return this.validHashes.has(modelHash) ? null : modelHash;
  }

  private getHash(row: FlatCompatibleValue[]): Hash {
    return row.map(v => isOption(v) ? v._id : v).join('##');
  }

  private addRuleHash(rule: CompatibilityRule) {
    const flatValue = this.getRuleRowValue(rule);
    const ruleHash = this.getHash(flatValue);
    this.validHashes.add(ruleHash)
  }

  isCompatibilitySwitchedOff(model: Model) {
    return !this.whenExpression(model);
  }

  getCompatibilityInvolvedOptionPaths(): Set<PropertyPath> {
    return new Set(this.optionsColumns.keys());
  }

  private getColumnOptionPath(column: PropertyPath) {
    let optionPath = '';
    this.typeService.findTypeInPropertyPath(column, (type, parentType, propertyName, path) => {
      if (parentType && objectTypeTypeGuard(type) && objectTypeTypeGuard(parentType)) {
        const sourcedValueProperty = parentType.properties.find(property => property.name == propertyName && property.opts.needSource);
        if (sourcedValueProperty) {
          optionPath = TypeService.absolutePathToRelative(path)
          return true;
        }
      }
      return false;
    })
    return optionPath;
  }

  createOptionPropertiesSelector(optionPath: PropertyPath):
    { optionHashSelector: OptionHashSelector, ruleHashSelector: RuleHashSelector } {
    const optionFullPathProperties = this.optionsColumns.get(optionPath);
    const optionProperties = [];
    for (const column of optionFullPathProperties) {
      const shortColumnPath = column.slice(optionPath.length + 1);
      optionProperties.push(shortColumnPath);
    }
    const ruleIndexes = [];
    for (let column of optionFullPathProperties) {
      ruleIndexes.push(this.columnName.get(column));
    }

    return {
      ruleHashSelector: (rule) => {
        const valueForHash = [];
        for (let i of ruleIndexes) {
          const ruleValue = rule[i];
          if (pOptionTypeGuard(ruleValue)) {
            valueForHash.push(ruleValue._id)
          } else {
            valueForHash.push(ruleValue);
          }
        }
        return this.getHash(valueForHash);
      },
      optionHashSelector: option => {
        const valuesForHash = [];
        for (let column of optionProperties) {
          if (column === '' && pOptionTypeGuard(option)) {
            valuesForHash.push(option._id);
          } else {
            valuesForHash.push(engineUtils.getPropertyPathValue(option.model, column));
          }
        }
        return this.getHash(valuesForHash);
      }
    }
  }

  private buildOptionColumns() {
    const paths = new Set<PropertyPath>();
    for (let column of this.columnName.keys()) {
      const optionPath = this.getColumnOptionPath(column);
      if (optionPath) {
        let columns = this.optionsColumns.get(optionPath);
        if (!columns) {
          columns = new Set<ColumnName>();
          this.optionsColumns.set(optionPath, columns);
        }
        columns.add(column);
      }
    }
    return paths;
  }
}

export type OptionHashSelector = (option: Option) => Hash;
export type RuleHashSelector = (rule: CompatibilityRule) => Hash;

export interface CompatibleTableStructure {
  columns: ColumnName[],
}


